/**
 * CORTEX API service — thin facade over @cortex/domain's typed client.
 *
 * Production paths (VITE_API_BASE_URL is set):
 *   loadConfig()        → GET  /engine/config
 *   calculatePremium()  → POST /quotes/calculate
 *   getConfig()         → synchronous read of the cached config
 *
 * Dev-only fallback (VITE_API_BASE_URL not set, auth.state === 'unconfigured'):
 *   authenticate()      → matches against MOCK_USERS in constants.ts
 *   requestAccess()     → no-op toast stub
 *
 * All localStorage stubs (users, companies, audit, config persistence)
 * were removed in PR #5l. Those domains are served by live API endpoints:
 *   users/entities  → /admin/entities, /admin/users
 *   audit log       → /admin/audit
 *   sectors         → /admin/sectors
 *   broker list     → /admin/entities?entityTypeSlug=brokerage
 */

import { type LombardEngineConfig, type UserProfile, type RegistrationRequest } from '../types';
import { MOCK_USERS } from '../constants';
import { cortexApi, isApiConfigured } from './cortexApi';
import type { AppliedFacility, QuoteCalculateRequest } from '@cortex/domain';
import type { CurrentPolicyContext } from '../types';

/**
 * Result of a successful POST /quotes/calculate.
 *
 * `appliedFacility` is non-null when the calling user belongs to a
 * brokerage with an active FacilityAgreement. Internal Lombard
 * underwriters receive `null` — they price the technical book rate.
 */
export interface CalculatePremiumResult {
  readonly context: CurrentPolicyContext;
  readonly appliedFacility: AppliedFacility | null;
}

class LhrApiService {
  private config: LombardEngineConfig | null = null;

  /** Fetch engine config from the API if not already in memory. */
  public async loadConfig(): Promise<LombardEngineConfig> {
    if (this.config) return this.config;

    if (!isApiConfigured()) {
      throw new Error(
        'VITE_API_BASE_URL is not set. Start the API (`npm run dev:api`) and configure .env.local.',
      );
    }

    const response = await cortexApi.engine.getConfig();
    this.config = response.config as unknown as LombardEngineConfig;
    return this.config;
  }

  /** Synchronous read of the cached config. Must call loadConfig() first. */
  public getConfig(): LombardEngineConfig {
    if (!this.config) {
      throw new Error('Engine config not loaded. Call loadConfig() after login.');
    }
    return this.config;
  }

  /**
   * Run server-side rating and return the priced context and any
   * facility-overlay metadata.
   */
  public async calculatePremium(context: CurrentPolicyContext): Promise<CalculatePremiumResult> {
    if (!isApiConfigured()) {
      throw new Error('VITE_API_BASE_URL is not set. Quote calculation requires the CORTEX API.');
    }

    await this.loadConfig();

    const response = await cortexApi.quotes.calculate({
      context: context as unknown as QuoteCalculateRequest['context'],
      config: this.config ? (this.config as unknown as Record<string, unknown>) : undefined,
    });

    return {
      context: response.context as unknown as CurrentPolicyContext,
      appliedFacility: response.appliedFacility,
    };
  }

  // ─── Dev-only mock auth (only reachable when auth.state === 'unconfigured') ──
  //
  // These methods are used by LoginScreen, which is only rendered when
  // VITE_API_BASE_URL is not set (local dev without an API). They are
  // never reachable in production.

  public async authenticate(email: string): Promise<UserProfile | null> {
    await new Promise((r) => setTimeout(r, 500));
    const user = MOCK_USERS.find((u) => u.email.toLowerCase() === email.toLowerCase());
    if (user) {
      user.lastLogin = new Date().toISOString();
    }
    return user ?? null;
  }

  public async requestAccess(_req: RegistrationRequest): Promise<void> {
    await new Promise((r) => setTimeout(r, 800));
  }
}

export const lhrApiService = new LhrApiService();
